import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { t } from 'i18next';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import FormHelperText from '@material-ui/core/FormHelperText';
import Radio from '@material-ui/core/Radio';
import Slide from '@material-ui/core/Slide';
import CloseIcon from '@material-ui/icons/Close';
import EditIcon from '@material-ui/icons/Edit';
import BereavedPersonCard from 'components/common/cards/BereavedPersonCard';
import AshesRecordCard from 'components/bereavement/AshesRecordCard';
import StepNavigation from 'components/common/StepNavigation';
import InvoiceLoad from 'icons/InvoiceLoad';
import InvoiceLoadStatic from 'icons/InvoiceLoad_static';
import { bereavedPersonConnectionType, ashesRecordType } from 'types/bereavement';

import styles from 'scss/main.scss';
import rcStyles from './styles.scss';

const Transition = React.forwardRef((props, ref) => <Slide direction="down" ref={ref} {...props} />);

const AshesReleaseFormModal = ({
  activeStep,
  relatedData,
  isOpen,
  ashesRecords,
  bereavedPeopleConnections,
  selectedAshesRecords,
  selectedClient,
  isReleaseFormDownloading,
  onChangeActiveStep,
  onChangeAshes,
  onChangeClient,
  onSaveAshes,
  onClose,
  onGenerateReleaseForm,
  errors,
}) => (
  <Dialog
    onClose={onClose}
    fullScreen
    open={isOpen}
    TransitionComponent={Transition}
    className={styles.c_dialogue}
    disableBackdropClick
    disableEscapeKeyDown
  >
    <DialogTitle
      disableTypography
      className={styles.dialog_title_bar}
    >
      <IconButton
        className={classNames(
          styles.o_button__secondary,
          styles.is_close,
        )}
        color="primary"
        variant="contained"
        aria-label="Close"
        onClick={onClose}
        data-test-id="closeModal"
      >
        <CloseIcon />
      </IconButton>
      <div
        className={styles.is_title}
        data-test-id="release-form-title"
      >
        {t('Ashes release form')}
      </div>
      <div className={styles.is_actions}>
        <Button
          color="secondary"
          variant="contained"
          onClick={() => {
            if (activeStep === 0) {
              return onSaveAshes();
            }
            if (activeStep === 1) {
              return onChangeActiveStep(2);
            }
            return onClose();
          }}
          disabled={false}
          data-test-id="changeStep"
        >
          {activeStep !== 2 && t('Next')}
          {activeStep === 2 && t('Close')}
        </Button>
      </div>
    </DialogTitle>

    <StepNavigation
      activeStep={activeStep}
      navigationSteps={[
        { title: 'Select ashes records' },
        { title: 'Select client' },
        { title: 'Create form' },
      ]}
      onChangeActiveStep={(step) => {
        if (activeStep === 0) {
          return onSaveAshes(step);
        }
        return onChangeActiveStep(step);
      }}
    />
    <div className={styles.o_view}>
      {activeStep === 0 && (

        <div className={styles.o_view__main}>
          {errors.ashes && (
            <FormHelperText error className={rcStyles.error}>
              {errors.ashes}
            </FormHelperText>
          )}
          {ashesRecords.map(ashesRecord => (
            <div
              key={ashesRecord.id}
              className={rcStyles.ashes_item}
            >
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={selectedAshesRecords.includes(ashesRecord.id)}
                    onChange={event => onChangeAshes(ashesRecord.id, event.target.checked)}
                    inputProps={{ 'data-test-id': `possessionCheck-${ashesRecord.id}` }}
                    color="primary"
                    classes={{ root: styles.u_hard__left }}
                  />
                )}
              />
              <AshesRecordCard
                key={ashesRecord.id}
                ashesRecord={ashesRecord}
                relatedData={relatedData}
                bereavedPeopleConnections={bereavedPeopleConnections}
              />
            </div>
          ))}
        </div>
      )}
      {activeStep === 1 && (
        <div className={styles.o_view__main}>
          {bereavedPeopleConnections.map(bereavedPersonConnection => (
            <div
              key={bereavedPersonConnection.id}
              className={rcStyles.client_select}
            >
              <Radio
                color="primary"
                name="client"
                value={bereavedPersonConnection.id}
                checked={selectedClient === bereavedPersonConnection.id}
                onChange={event => onChangeClient(event.target.value)}
                data-test-id={bereavedPersonConnection.id}
                className={styles.u_push__right}
              />
              <BereavedPersonCard
                bereavedPersonConnection={bereavedPersonConnection}
                customClass={rcStyles.bereaved_card}
              />
            </div>
          ))}
        </div>
      )}
      {activeStep === 2 && (
        <div className={styles.o_view__main}>
          <div className={rcStyles.generate_release_form}>
            <div className={rcStyles.is_media}>
              {isReleaseFormDownloading ? (
                <InvoiceLoad />
              ) : (
                <InvoiceLoadStatic />
              )}
            </div>
            <div className={rcStyles.is_buttons}>
              <Button
                onClick={() => onChangeActiveStep(0)}
                color="secondary"
                variant="contained"
                data-test-id="backToReleaseForm"
              >
                <EditIcon />
                {t('Edit form')}
              </Button>
              <Button
                onClick={onGenerateReleaseForm}
                color="primary"
                variant="contained"
                data-test-id="confirmGenerateInvoice"
              >
                {isReleaseFormDownloading ? (
                  <Fragment>
                    {t('Generating release form')}
                  </Fragment>
                ) : (
                  <Fragment>
                    {t('Download')}
                  </Fragment>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  </Dialog>
);

AshesReleaseFormModal.propTypes = {
  activeStep: PropTypes.number.isRequired,
  relatedData: PropTypes.objectOf(PropTypes.any),
  isOpen: PropTypes.bool.isRequired,
  ashesRecords: PropTypes.arrayOf(ashesRecordType).isRequired,
  bereavedPeopleConnections: PropTypes.arrayOf(bereavedPersonConnectionType).isRequired,
  selectedAshesRecords: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  selectedClient: PropTypes.string.isRequired,
  isReleaseFormDownloading: PropTypes.bool.isRequired,
  onChangeActiveStep: PropTypes.func.isRequired,
  onChangeAshes: PropTypes.func.isRequired,
  onChangeClient: PropTypes.func.isRequired,
  onSaveAshes: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onGenerateReleaseForm: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default AshesReleaseFormModal;
